import React from "react";
import TopBanner from "../components/TopBanner";
import { CTAButton, CTAButtonWrapper } from "../components/HeroSection";
import { ContentWrapper } from "./InsuranceGuidance";
import { Card, Cards, Icon } from "../components/WhoWeAre";
import { CardText, CardTitle } from "../components/WhyChooseUS";
import styled from "styled-components";
import FAQ from "../components/FAQ";

const HealthInsurance = ({ openModal }) => {
  React.useEffect(() => {
    document.title =
      "Health Insurance Guide – Meaning, Coverage, Eligibility & Tax Benefits in India | Wealthies";
  }, []);

  const faqData = [
    {
      question: "Is health insurance mandatory in India?",
      answer:
        "Health insurance is not legally mandatory for individuals in India, but many people consider it for financial protection against medical expenses.",
    },
    {
      question: "Can parents be covered under health insurance?",
      answer:
        "Yes, some policies allow coverage for parents either through family floater plans or separate policies.",
    },
    {
      question: "Is medical check-up required before buying health insurance?",
      answer:
        "Some insurers may require medical tests depending on age, coverage amount, or medical history.",
    },
    {
      question: "Can health insurance be purchased online?",
      answer:
        "Many insurance companies allow online policy purchase, subject to verification and policy conditions.",
    },
  ];

  return (
    <>
      <TopBanner content={"Health Insurance"} />
      <ContentWrapper>
        <div className="text-content">
          <h2>Introduction</h2>
          <p>
            Medical expenses can arise unexpectedly, and healthcare costs have
            increased significantly over time. Health insurance is designed to
            help individuals and families manage certain medical expenses by
            providing financial coverage for eligible treatments and
            hospitalisation.
          </p>
          <p>
            A health insurance policy generally helps cover expenses such as
            hospital bills, surgeries, and certain medical treatments, depending
            on the policy terms and conditions.
          </p>
          <p>
            Understanding how health insurance works can help individuals
            evaluate coverage options and prepare for medical expenses.
          </p>
          <p>
            This guide explains the basics of health insurance, including
            eligibility, types of plans, coverage features, and potential tax
            benefits available under Indian tax laws.
          </p>

          <h3>
            What is <span>Health Insurance</span>
          </h3>
          <p>
            Health insurance is a type of insurance policy that helps cover
            certain medical expenses incurred due to illness, injury, or
            hospitalisation.
          </p>
          <p>
            The policyholder pays a premium to the insurance company. In return,
            the insurer may provide financial coverage for eligible medical
            expenses as defined in the policy document.
          </p>
          <p>
            Health insurance policies typically cover expenses such as
            hospitalisation costs, surgery expenses, pre- and post-
            hospitalisation treatments, and day-care procedures (subject to
            policy terms). Coverage details vary between insurers and policies.
          </p>

          <h3>
            Why <span>Health Insurance</span> is Important
          </h3>
          <p>
            Healthcare costs can sometimes place a financial burden on families,
            especially in case of unexpected medical emergencies.
          </p>
          <p>
            Health insurance can help individuals and families manage these
            expenses and provide access to medical treatment when needed.
          </p>
          <p>Some reasons why people consider health insurance include:</p>
          <ul>
            <li>Managing unexpected hospital expenses</li>
            <li>
              Access to medical treatment without immediate financial stress
            </li>
            <li>Financial support during medical emergencies</li>
            <li>Coverage for certain treatments and procedures</li>
          </ul>
          <p>
            The suitability of health insurance depends on personal needs and
            financial planning goals.
          </p>

          <h3>
            Eligibility for <span>Health Insurance</span> in India
          </h3>
          <p>
            Eligibility criteria for health insurance may vary between insurance
            companies, but general guidelines often include the following.
          </p>

          <h4>Age Eligibility</h4>
          <p>
            Most insurers allow individuals to purchase health insurance
            starting from a young age.
          </p>
          <ul>
            <li>
              Minimum entry age: often around 18 years for individual policies
            </li>
            <li>
              Children may be covered under family plans from around 90 days of
              age, subject to policy conditions.
            </li>
            <li>Maximum entry age may vary depending on the insurer.</li>
          </ul>

          <h4>Medical History and Health Condition</h4>
          <p>
            Insurance companies may evaluate the applicant’s health condition
            and medical history before issuing the policy.
          </p>
          <p>
            Certain pre-existing conditions may have waiting periods before
            coverage begins.
          </p>

          <h4>Documentation Requirements</h4>
          <p>
            Applicants generally need to submit identity and personal documents
            such as:
          </p>
          <ul>
            <li>Aadhaar card</li>
            <li>PAN card</li>
            <li>Address proof</li>
            <li>Age proof</li>
          </ul>
          <p>
            Additional medical information may be requested depending on the
            policy.
          </p>

          <h3>
            Types of <span>Health Insurance</span> Plans
          </h3>
          <Cards>
            <Card>
              <Icon className="fas fa-user"></Icon>
              <CardTitle>Individual Health Insurance</CardTitle>
              <CardText>
                Individual health insurance provides coverage for one person
                under the policy. The sum insured applies only to that
                individual.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-users"></Icon>
              <CardTitle>Family Floater Health Insurance</CardTitle>
              <CardText>
                Family floater plans provide coverage for multiple family
                members under a single policy. The total sum insured is shared
                among all insured family members.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-user-plus"></Icon>
              <CardTitle>Senior Citizen Health Insurance</CardTitle>
              <CardText>
                Some insurers offer specialised policies designed for
                individuals above a certain age, often focusing on healthcare
                needs during retirement years.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-heartbeat"></Icon>
              <CardTitle>Critical Illness Insurance</CardTitle>
              <CardText>
                Critical illness policies provide coverage for specific serious
                illnesses listed in the policy, subject to terms.
              </CardText>
            </Card>
          </Cards>

          <h3>
            What is Usually <span>Covered</span> in Health Insurance?
          </h3>
          <p>
            Health insurance policies may cover several medical expenses
            depending on policy terms.
          </p>
          <List>
            <li>Hospitalisation expenses</li>
            <li>Surgery costs</li>
            <li>Room rent charges</li>
            <li>Pre-hospitalisation expenses</li>
            <li>Post-hospitalisation expenses</li>
            <li>Ambulance charges (subject to limits)</li>
          </List>
          <p>Exact coverage depends on policy terms and insurer guidelines.</p>

          <h3>
            Understanding <span>Waiting</span> Periods
          </h3>
          <p>
            Many health insurance policies include waiting periods for certain
            conditions.
          </p>
          <p>
            A waiting period is the time during which certain conditions or
            treatments may not be covered.
          </p>
          <ul>
            <li>Pre-existing disease waiting period</li>
            <li>Specific illness waiting period</li>
            <li>Maternity waiting period</li>
          </ul>
          <p>Waiting periods vary between insurers and policy types.</p>

          <h3>
            Factors That <span>Affect</span> Health Insurance Premiums
          </h3>
          <Cards>
            <Card>
              <Icon className="fas fa-user-clock"></Icon>
              <CardTitle>Age of the Insured</CardTitle>
              <CardText>
                Older individuals may have higher premiums due to increased
                health risk.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-notes-medical"></Icon>
              <CardTitle>Medical History</CardTitle>
              <CardText>
                Existing health conditions and lifestyle habits may influence
                premium calculations.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-layer-group"></Icon>
              <CardTitle>Sum Insured</CardTitle>
              <CardText>
                Higher coverage amounts usually result in higher premiums.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-users"></Icon>
              <CardTitle>Number of Family Members</CardTitle>
              <CardText>
                In family floater plans, premiums may depend on the number of
                insured members.
              </CardText>
            </Card>
          </Cards>

          <h3>
            Tax Benefits of <span>Health Insurance</span> in India
          </h3>
          <h5>Section 80D – Health Insurance Deduction</h5>
          <p>
            Premiums paid for health insurance may qualify for deductions under
            Section 80D of the Income Tax Act.
          </p>
          <p>
            The deduction may apply for policies covering self, spouse,
            dependent children, and parents.
          </p>
          <p>
            Deduction limits may vary based on age of insured persons and
            prevailing tax regulations. Tax benefits are subject to changes in
            tax laws and individual eligibility conditions.
          </p>

          <h3>
            Common Mistakes <span>People Make When Buying</span> Health
            Insurance
          </h3>
          <ul>
            <li>Delaying purchase</li>
            <li>Choosing insufficient coverage</li>
            <li>Ignoring waiting periods</li>
            <li>Not checking network hospitals</li>
          </ul>

          <h3>
            Frequently <span>Asked</span> Questions
          </h3>
          <FAQ faqData={faqData} />
          <h3>
            How <span>Wealthies</span> Helps
          </h3>
          <p>
            Wealthies aims to simplify insurance awareness by helping
            individuals understand insurance concepts in simple language.
          </p>
          <ul>
            <li>Learn about health insurance coverage</li>
            <li>Understand key factors before choosing a policy</li>
            <li>
              Connect with licensed insurance professionals for further guidance
            </li>
            <li>Receive support assistance through Wealthies Shield</li>
          </ul>

          <p>Need help understanding health insurance options?</p>
          <p>
            Our team can help explain the basics and connect you with licensed
            insurance professionals who can guide you further.
          </p>

          <CTAButtonWrapper>
            <CTAButton onClick={openModal}>
              Get Free Insurance Guidance
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </ContentWrapper>
    </>
  );
};

export default HealthInsurance;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    position: relative;
    padding-left: 32px;
    margin-bottom: 12px;
    font-size: 16px;
    color: #2c2c2c;
    line-height: 1.5;
  }

  li::before {
    content: "✔";
    position: absolute;
    left: 0;
    top: 4px;

    width: 18px;
    height: 18px;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #2e7d32; /* premium green */
    color: #fff;
    border-radius: 50%;
    font-size: 12px;
    font-weight: bold;

    box-shadow: 0 2px 6px rgba(46, 125, 50, 0.3);
  }
`;
